import classNames from 'classnames';
import React, { ReactNode, useRef, useState } from 'react';

import {
  Avatar,
  CommonProps,
  List,
  ListItemStandard,
  Popover,
  Title,
} from '@ui5/webcomponents-react';

import sapLogo from '../../assets/images/sap-logo.svg';
import { VersionLabel } from '../index';
import { Typography } from '../Typography';
import Divider from './Divider';
import styles from './ShellBar.module.css';
import { VersionLabelText } from '../VersionLabel/VersionLabel';

export interface ShellBarProps extends CommonProps {
  /**
   * Action buttons
   */
  actionButtons: React.JSX.Element | React.JSX.Element[];
  /**
   * sets aria-label for the nav
   */
  'aria-label'?: string;
  /**
   * Avatar contents - user initials or Avatar component
   */
  avatar: ReactNode;
  /**
   * sets href for the main title
   */
  href: string;
  /**
   * Main action links
   */
  mainActionLinks?: React.JSX.Element | React.JSX.Element[];
  /**
   * Header title
   */
  mainTitle: string;
  /**
   * sets on sign out function
   */
  onSignOut: () => void;
  /**
   * sets privacy statement url
   */
  privacyStatementUrl: string;
  /**
   * sets user display name
   */
  userDisplayName: string;
  /**
   * sets version label text text
   */
  versionLabelText?: VersionLabelText;
}

export function ShellBar(props: ShellBarProps) {
  const [preferencesMenuOpen, setPreferenceMenuOpen] = useState(() => false);
  const {
    'aria-label': ariaLabel,
    actionButtons,
    avatar,
    className,
    href,
    versionLabelText,
    mainActionLinks,
    mainTitle,
    onSignOut,
    userDisplayName,
    privacyStatementUrl,
  } = props;
  const openPreferencesButtonRef = useRef(null);
  const shellBarStyles = classNames(className, {
    'fd-shellbar': true,
    [styles['shell-bar']]: true,
  });
  const mainActionLinkContainer = classNames({
    [styles['main-action-link-container']]: true,
  });

  return (
    <nav
      aria-label={ariaLabel}
      className={shellBarStyles}
    >
      <div
        className="fd-shellbar__group fd-shellbar__group--product"
        style={{ minWidth: '370px' }}
      >
        <a
          className="fd-shellbar__logo"
          href={href}
        >
          <picture>
            <img
              alt="SAP logo"
              src={sapLogo}
            />
          </picture>
        </a>
        <Typography
          className="fd-shellbar__title"
          tag="h1"
          variant="header1"
        >
          Build
        </Typography>
        <Typography
          tag="h2"
          variant="header2"
        >
          Apps
          {mainTitle && (
            <>
              <Divider />
              {mainTitle}
            </>
          )}
        </Typography>
        {versionLabelText && <VersionLabel>{versionLabelText}</VersionLabel>}
      </div>
      <div className="fd-shellbar__group fd-shellbar__group--actions fd-shellbar__group--basis-auto">
        {mainActionLinks && (
          <div className={mainActionLinkContainer}>{mainActionLinks}</div>
        )}
        {actionButtons}
        <div className="fd-shellbar__action">
          <div className="fd-popover fd-popover--right">
            <div className="fd-popover__control">
              <button
                aria-expanded={preferencesMenuOpen ? 'true' : 'false'}
                aria-haspopup="true"
                aria-label="Open user actions menu"
                className="fd-button fd-button--transparent fd-shellbar__button fd-user-menu__control"
                onClick={() => setPreferenceMenuOpen((prev) => !prev)}
                ref={openPreferencesButtonRef}
                tabIndex={0}
                type="button"
              >
                {typeof avatar === 'string' ? (
                  <Avatar
                    aria-label="User initials"
                    initials={avatar}
                    size="XS"
                  />
                ) : (
                  avatar
                )}
              </button>
            </div>
            <Popover
              className={styles['user-menu-popover']}
              horizontalAlign="End"
              onClose={() => setPreferenceMenuOpen(false)}
              open={preferencesMenuOpen}
              opener={openPreferencesButtonRef.current ?? undefined}
              placement="Bottom"
              verticalAlign="Top"
            >
              <div className={styles['user-menu-header']}>
                <Title level="H5">{userDisplayName}</Title>
              </div>
              <List
                onItemClick={(e) => {
                  if (e.detail.item.dataset.key === 'signout') {
                    onSignOut();
                  } else if (e.detail.item.dataset.key === 'privacy-statement') {
                    window.open(privacyStatementUrl, '_blank');
                  }

                  setPreferenceMenuOpen(false);
                }}
                separators="None"
              >
                <ListItemStandard
                  data-key="privacy-statement"
                  icon="official-service"
                >
                  Privacy Statement
                </ListItemStandard>
                <ListItemStandard
                  data-key="signout"
                  icon="log"
                >
                  Sign Out
                </ListItemStandard>
              </List>
            </Popover>
          </div>
        </div>
      </div>
    </nav>
  );
}
